import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string | undefined;

type VerifyState = "loading" | "success" | "error";

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<VerifyState>("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) {
      setState("error");
      setErrorMessage("Missing verification token. Please check your email link.");
      return;
    }

    const controller = new AbortController();
    const url = API_BASE_URL
      ? `${API_BASE_URL}/public/verify`
      : "/public/verify";

    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
      signal: controller.signal,
    })
      .then(async (res) => {
        if (res.ok) {
          setState("success");
        } else {
          const body = await res.json().catch(() => ({}));
          setState("error");
          setErrorMessage(
            body?.message || "Verification failed. The link may have expired.",
          );
        }
      })
      .catch((err) => {
        if ((err as Error).name === "AbortError") return;
        setState("error");
        setErrorMessage("Network error — please check your connection and try again.");
      });

    return () => controller.abort();
  }, [searchParams]);

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background">
      <GridBg />
      <div
        className={`pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[120px] ${
          state === "success"
            ? "bg-primary/[0.06]"
            : state === "error"
              ? "bg-red-500/[0.06]"
              : "bg-primary/[0.06]"
        }`}
      />

      <div
        className={`relative z-10 w-full max-w-[400px] transition-all duration-700 ease-out ${mounted ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}
      >
        <div
          className={`rounded-2xl bg-gradient-to-b p-px ${
            state === "success"
              ? "from-primary/20 via-primary/5 to-transparent"
              : state === "error"
                ? "from-red-500/20 via-red-500/5 to-transparent"
                : "from-primary/20 via-primary/5 to-transparent"
          }`}
        >
          <div className="rounded-2xl bg-card/90 px-8 pb-8 pt-10 backdrop-blur-xl">
            {/* Icon */}
            <div className="mb-6 flex justify-center">
              <div className="relative">
                <div
                  className={`absolute -inset-3 rounded-full blur-md ${
                    state === "success"
                      ? "bg-primary/10"
                      : state === "error"
                        ? "bg-red-500/10"
                        : "bg-primary/10"
                  }`}
                />
                <div
                  className={`relative flex h-16 w-16 items-center justify-center rounded-full border ${
                    state === "success"
                      ? "border-primary/20 bg-primary/[0.08]"
                      : state === "error"
                        ? "border-red-500/20 bg-red-500/[0.08]"
                        : "border-primary/20 bg-primary/[0.08]"
                  }`}
                >
                  {state === "loading" && (
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  )}
                  {state === "success" && (
                    <CheckIcon className="h-8 w-8 text-primary" />
                  )}
                  {state === "error" && (
                    <XIcon className="h-8 w-8 text-red-400" />
                  )}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="mb-6 text-center">
              {state === "loading" && (
                <>
                  <h1 className="mb-1.5 text-[22px] font-semibold tracking-tight text-white">
                    Verifying your email
                  </h1>
                  <p className="text-[13px] text-slate-400">
                    Please wait while we verify your account...
                  </p>
                </>
              )}
              {state === "success" && (
                <>
                  <h1 className="mb-1.5 text-[22px] font-semibold tracking-tight text-white">
                    Email verified
                  </h1>
                  <p className="text-[13px] text-slate-400">
                    Your account has been created. We sent a welcome email with
                    your temporary password — sign in below to get started.
                  </p>
                </>
              )}
              {state === "error" && (
                <>
                  <h1 className="mb-1.5 text-[22px] font-semibold tracking-tight text-white">
                    Verification failed
                  </h1>
                  <p className="text-[13px] text-slate-400">{errorMessage}</p>
                </>
              )}
            </div>

            {/* Actions */}
            {state === "success" && (
              <Link to="/login">
                <Button
                  size="lg"
                  className="w-full rounded-xl border-0 bg-primary py-6 text-[14px] font-medium tracking-wide text-white shadow-lg shadow-primary/20 transition-all duration-300 hover:bg-primary hover:shadow-primary/30"
                >
                  Go to Sign In
                </Button>
              </Link>
            )}
            {state === "error" && (
              <Link to="/signup">
                <Button
                  size="lg"
                  className="w-full rounded-xl border-0 bg-primary py-6 text-[14px] font-medium tracking-wide text-white shadow-lg shadow-primary/20 transition-all duration-300 hover:bg-primary hover:shadow-primary/30"
                >
                  Try Again
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      <div
        className={`absolute bottom-6 text-center text-[11px] tracking-widest text-slate-600 transition-all delay-300 duration-700 ${mounted ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"}`}
      >
        PRAYINGMANTIS.CLOUD
      </div>
    </div>
  );
}

function GridBg() {
  return (
    <div
      className="pointer-events-none absolute inset-0 opacity-[0.04]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(59,130,246,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.5) 1px, transparent 1px)",
        backgroundSize: "48px 48px",
      }}
    />
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}
